/**
 * The brief for Claude, as a task tells it: what the Claude modal's prompt
 * starts as, and what a task sent back to "todo" from the tmux view is
 * queued with again. Laid out by the admin's template (Integrations menu,
 * shared/taskPrompt.ts); this only works out what each tag stands for.
 */
import { taskLink } from "./taskRoute.js";
import { repoTaskPromptTemplates, taskPromptTemplate } from "./taskPromptTemplate.js";
import { useBoard } from "../stores/board.js";
import { fillTaskPrompt, parentsValues } from "@shared/taskPrompt.js";
import type { Task } from "@shared/types.js";

function statusList(head: string, tasks: Task[]): string {
  return tasks.length ? [head, ...tasks.map((c) => `- [${c.status}] ${c.title}`)].join("\n") : "";
}

/**
 * Its ancestors come first, root down, each with its notes — a sub-task's
 * title rarely makes sense without what it is part of. Unfinished sub-tasks
 * are what is left to do; finished ones are context, as are the tasks it
 * waits on and those waiting on it (only ones this reader can see). Without `template`, the
 * task's repo's own template if it has one, else the server-wide one.
 */
export function taskPrompt(t: Task, template?: string): string {
  const board = useBoard();
  const chain: Task[] = [];
  for (let p = t.parentId ? board.tasks.get(t.parentId) : undefined; p; p = p.parentId ? board.tasks.get(p.parentId) : undefined) {
    chain.unshift(p);
  }
  const kids = board.kids.get(t.id) ?? [];
  const deps = t.blockedBy.flatMap((id) => board.tasks.get(id) ?? []);
  const dependants = [...board.tasks.values()].filter((o) => o.blockedBy.includes(t.id));
  const repo = [t, ...[...chain].reverse()].find((a) => a.repo)?.repo ?? "";
  return fillTaskPrompt(template ?? (repoTaskPromptTemplates.value[repo] || taskPromptTemplate.value), {
    ...parentsValues(chain),
    title: t.title,
    notes: t.notes.trim(),
    subtasks: kids.length ? ["Sub-tasks:", ...kids.map((c) => `- [${c.status}] ${c.title}`)].join("\n") : "",
    dependencies: statusList("It waits on:", deps),
    dependants: statusList("Waiting on it:", dependants),
    repo,
    id: t.id,
    link: taskLink(t.id),
  });
}
